import { Blueprint } from '@/components/ui/Blueprint';
import { CardKicker } from '@/components/ui/Card';
import { MonoData } from '@/components/ui/MonoData';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { ptBR } from '@/i18n/pt-BR';
import type {
  PhysicalValueData,
  SignatureRecordData,
  SignaturePhysicalValuesData,
} from '@/lib/case_signatures';
import {
  useSignaturePhysicalValues,
  type SignaturePhysicalValuesState,
} from './useSignaturePhysicalValues';

export interface PhysicalMatrixPanelProps {
  caseId: string;
  contested: SignatureRecordData;
  selectedPattern: SignatureRecordData | null;
  /** Controlado pelo seletor segmentado (E29.F1): o corpo só monta quando aberto. */
  open: boolean;
}

/**
 * Matriz de convergência física — valores medidos K|Q (E26.F3/ADR-0038). // ux-neutrality:allow doc-comment
 *
 * Painel colapsado por padrão (payload buscado on-demand). Honestidade por // ux-neutrality:allow doc-comment
 * construção: valores do MESMO extrator do motor; px exibido na unidade // ux-neutrality:allow doc-comment
 * canônica ×espessura-do-traço com o cru entre parênteses (ADR-0013 — px // ux-neutrality:allow doc-comment
 * absolutos não comparam entre capturas); NENHUMA cor/marcação de veredito // ux-neutrality:allow doc-comment
 * na matriz — a leitura comparativa é do perito (ADR-0026 preservada). // ux-neutrality:allow doc-comment
 *
 * E54.F6.T08 — a tabela passa ao primitivo do pacote e o valor medido a mono, // ux-neutrality:allow doc-comment
 * que é o que permite comparar duas linhas sem lê-las (prancha 2C). // ux-neutrality:allow doc-comment
 */
export function PhysicalMatrixPanel({
  caseId,
  contested,
  selectedPattern,
  open,
}: PhysicalMatrixPanelProps): JSX.Element | null {
  if (!open) return null;

  return (
    <section className="matrix-panel" aria-label={ptBR.workspaceCaso.matrixTitle}>
      <MatrixBody caseId={caseId} contested={contested} selectedPattern={selectedPattern} />
    </section>
  );
}

function MatrixBody({
  caseId,
  contested,
  selectedPattern,
}: Omit<PhysicalMatrixPanelProps, 'open'>): JSX.Element {
  const pattern = useSignaturePhysicalValues(caseId, selectedPattern?.id);
  const quest = useSignaturePhysicalValues(caseId, contested.id);

  return (
    <Blueprint className="matrix-body" id="matrix-body" data-testid="matrix-body">
      <CardKicker>{ptBR.workspaceCaso.matrixTitle}</CardKicker>
      <div className="mt-sc-4">
        <MatrixState pattern={pattern} quest={quest} />
      </div>
      <p className="matrix-disclaimer">{ptBR.workspaceCaso.matrixDisclaimer}</p>
    </Blueprint>
  );
}

function MatrixState({
  pattern,
  quest,
}: {
  pattern: SignaturePhysicalValuesState;
  quest: SignaturePhysicalValuesState;
}): JSX.Element {
  if (pattern.loading || quest.loading) {
    return (
      <div className="matrix-loading" role="status" data-testid="matrix-loading">
        {ptBR.workspaceCaso.matrixLoading}
      </div>
    );
  }
  return (
    <>
      <MatrixTable pattern={pattern} quest={quest} />
      {(pattern.data?.stroke_width_px === null || quest.data?.stroke_width_px === null) && (
        <p className="matrix-stroke-warning" data-testid="matrix-stroke-warning">
          {ptBR.workspaceCaso.matrixStrokeUnresolved}
        </p>
      )}
    </>
  );
}

/** Linhas = união (por key, na ordem do payload) das grandezas das duas colunas. */
function mergedRows(
  pattern: SignaturePhysicalValuesData | null,
  quest: SignaturePhysicalValuesData | null,
): Array<{ key: string; label: string }> {
  const rows: Array<{ key: string; label: string }> = [];
  const seen = new Set<string>();
  for (const source of [quest, pattern]) {
    for (const v of source?.values ?? []) {
      if (seen.has(v.key)) continue;
      seen.add(v.key);
      rows.push({ key: v.key, label: v.label });
    }
  }
  return rows;
}

function MatrixTable({
  pattern,
  quest,
}: {
  pattern: SignaturePhysicalValuesState;
  quest: SignaturePhysicalValuesState;
}): JSX.Element {
  const rows = mergedRows(pattern.data, quest.data);
  if (rows.length === 0) {
    // F-E26.F3-02: falha técnica NÃO se apresenta como "indisponível" —
    // indisponibilidade é estado forense do material, não da operação.
    if (pattern.error || quest.error) {
      return (
        <p className="matrix-error" role="alert" data-testid="matrix-error">
          {ptBR.workspaceCaso.matrixError}
        </p>
      );
    }
    return <p className="matrix-unavailable">{ptBR.workspaceCaso.matrixUnavailable}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <Table className="matrix-table" data-testid="matrix-table">
        <TableHeader>
          <TableRow>
            <TableHead scope="col">{ptBR.workspaceCaso.matrixMeasureHeader}</TableHead>
            <TableHead scope="col">{ptBR.workspaceCaso.matrixPatternHeader}</TableHead>
            <TableHead scope="col">{ptBR.workspaceCaso.matrixContestedHeader}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key} data-testid={`matrix-row-${row.key}`}>
              {/* Cabeçalho de LINHA: `<th scope="row">` é o que dá o nome da
                  grandeza ao leitor de tela em cada célula da linha. O
                  primitivo só oferece `TableHead` de coluna. */}
              <th
                scope="row"
                className="border-b border-fg/[0.08] p-sc-2 text-left font-normal text-fg-muted"
              >
                {row.label}
              </th>
              <MatrixCell column={pattern} valueKey={row.key} />
              <MatrixCell column={quest} valueKey={row.key} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MatrixCell({
  column,
  valueKey,
}: {
  column: SignaturePhysicalValuesState;
  valueKey: string;
}): JSX.Element {
  if (column.error) {
    return (
      <TableCell className="matrix-cell--error">{ptBR.workspaceCaso.matrixCellError}</TableCell>
    );
  }
  const value = column.data?.values.find((v) => v.key === valueKey) ?? null;
  if (value === null) {
    return (
      <TableCell className="matrix-cell--unavailable">
        {ptBR.workspaceCaso.matrixCellUnavailable}
      </TableCell>
    );
  }
  return (
    <TableCell>
      {/* Valor medido é dado pericial: mono, para que duas linhas se comparem
          pelo alinhamento do dígito (regra do pacote). */}
      <MonoData>{formatPhysicalValue(value)}</MonoData>
    </TableCell>
  );
}

const _NUMBER_FORMAT = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

/** "20 ×traço (80 px)" para px normalizado; "−12,5°" para graus; número puro
 * para adimensionais/contagens — unidade sempre declarada (ADR-0038 §C/§E). */
export function formatPhysicalValue(value: PhysicalValueData): string {
  const raw = _NUMBER_FORMAT.format(value.value);
  if (value.unit === 'px') {
    if (value.normalized_by_stroke !== null) {
      return `${_NUMBER_FORMAT.format(value.normalized_by_stroke)} ${ptBR.workspaceCaso.matrixStrokeUnit} (${raw} px)`;
    }
    return `${raw} px`;
  }
  if (value.unit === 'graus') return `${raw}°`;
  return raw;
}
